'use client';

import { Suspense, useState } from 'react';
import { useParams, useRouter } from 'next/navigation';
import Link from 'next/link';
import Image from 'next/image';
import Header from '@/components/common/Header';
import Footer from '@/components/common/Footer';
import { useLanguage } from '@/contexts/LanguageContext';
import { getProductBySlug } from '@/data/products';

export const dynamic = 'force-dynamic';

const FALLBACK_IMAGE = '/assets/images/no_image.png';

function ProductImage({ src, alt }: { src: string; alt: string }) {
  const [imgSrc, setImgSrc] = useState(src || FALLBACK_IMAGE);
  const [isLoading, setIsLoading] = useState(true);
  const [zoomed, setZoomed] = useState(false);

  return (
    <div className="relative w-full bg-white border border-gray-200 rounded-xl overflow-hidden flex items-center justify-center" style={{ minHeight: '420px', maxHeight: '520px' }}>
      {/* Zoom icon */}
      <button
        onClick={() => setZoomed(!zoomed)}
        className="absolute top-3 right-3 z-20 w-9 h-9 bg-white border border-gray-200 rounded-full flex items-center justify-center shadow-sm hover:shadow-md transition-shadow"
        aria-label="Zoom image"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0zm-6-3v6m-3-3h6" />
        </svg>
      </button>

      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-white z-10">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {/* Zoom overlay */}
      {zoomed && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-6 cursor-zoom-out"
          onClick={() => setZoomed(false)}
        >
          <div className="relative w-full max-w-2xl max-h-[80vh] bg-white rounded-xl overflow-hidden">
            <Image
              src={imgSrc}
              alt={alt}
              width={800}
              height={600}
                className="object-contain w-full h-full p-6"
              style={{ maxHeight: '80vh' }}
            />
          </div>
        </div>
      )}

      <Image
        src={imgSrc}
        alt={alt || 'Product image'}
        fill
        className={`object-contain p-8 transition-all duration-300 ${isLoading ? 'opacity-0' : 'opacity-100'} ${!zoomed ? 'hover:scale-105' : ''}`}
        style={{ transition: 'transform 0.3s ease, opacity 0.3s ease' }}
        onLoad={() => setIsLoading(false)}
        onError={() => {
          setImgSrc(FALLBACK_IMAGE);
          setIsLoading(false);
        }}
        sizes="(max-width: 768px) 100vw, 50vw"
      />
    </div>
  );
}

function ProductDetailContent() {
  const params = useParams();
  const router = useRouter();
  const { t, language, dir } = useLanguage();

  const slug = params && typeof params.slug === 'string' ? params.slug : Array.isArray(params?.slug) ? params.slug[0] : '';
  const product = getProductBySlug(slug);

  const labels = {
    en: {
      home: 'Home',
      products: 'Products',
      notFound: 'Product not found',
      notFoundDesc: 'The product you are looking for does not exist.',
      backCatalog: 'Back to Catalog',
      origin: 'Origin',
      method: 'Fishing Method',
      processing: 'Processing',
      packaging: 'Packaging',
      requestQuote: 'Request a Quote',
      b2bNote: 'B2B inquiries only — wholesalers, importers, distributors & food service companies.',
      technicalSpecs: 'Technical Specifications',
      faoZone: 'FAO Zone',
    },
    fr: {
      home: 'Accueil',
      products: 'Produits',
      notFound: 'Produit introuvable',
      notFoundDesc: "Le produit que vous recherchez n\'existe pas.",
      backCatalog: 'Retour au Catalogue',
      origin: 'Origine',
      method: 'Méthode de Pêche',
      processing: 'Traitement',
      packaging: 'Conditionnement',
      requestQuote: 'Demander un Devis',
      b2bNote: 'Demandes B2B uniquement — grossistes, importateurs, distributeurs et restauration.',
      technicalSpecs: 'Spécifications Techniques',
      faoZone: 'Zone FAO',
    },
    ar: {
      home: 'الرئيسية',
      products: 'المنتجات',
      notFound: 'المنتج غير موجود',
      notFoundDesc: 'المنتج الذي تبحث عنه غير موجود.',
      backCatalog: 'العودة إلى الكتالوج',
      origin: 'المصدر',
      method: 'طريقة الصيد',
      processing: 'المعالجة',
      packaging: 'التعبئة',
      requestQuote: 'طلب عرض أسعار',
      b2bNote: 'استفسارات B2B فقط — تجار الجملة والمستوردون والموزعون وخدمات الطعام.',
      technicalSpecs: 'المواصفات التقنية',
      faoZone: 'منطقة FAO',
    },
  };

  const l = labels[language as keyof typeof labels] || labels.en;
  const lang = (language as 'en' | 'fr' | 'ar') || 'en';

  if (!product) {
    return (
      <main className="min-h-screen bg-background" dir={dir}>
        <Header />
        <section className="pt-40 pb-24 px-6 md:px-12 text-center">
          <h1 className="font-serif text-3xl font-bold text-foreground mb-4">{l.notFound}</h1>
          <p className="text-muted-foreground mb-8">{l.notFoundDesc}</p>
          <Link href="/products" className="btn-primary">
            {l.backCatalog}
          </Link>
        </section>
        <Footer />
      </main>
    );
  }

  const productName = t(product.nameKey) !== product.nameKey ? t(product.nameKey) : product.scientificName;
  const categoryLabel = t(product.categoryKey) !== product.categoryKey ? t(product.categoryKey) : product.category;

  // Extract FAO zone from origin
  const originValue = product.origin[lang] || product.origin.en;
  const faoMatch = originValue.match(/FAO\s*\d+/i);
  const faoZone = faoMatch ? faoMatch[0] : '';

  const specs = [
    { label: l.origin, value: originValue },
    { label: l.method, value: product.method[lang] || product.method.en },
    { label: l.processing, value: product.processing[lang] || product.processing.en },
    { label: l.packaging, value: product.packaging[lang] || product.packaging.en },
    ...(faoZone ? [{ label: l.faoZone, value: faoZone }] : []),
  ];

  const handleRequestQuote = () => {
    const encodedName = encodeURIComponent(productName);
    router.push(`/contact?product=${encodedName}`);
  };

  return (
    <main className="min-h-screen bg-background" dir={dir}>
      <Header />

      <section className="pt-28 pb-20 px-4 sm:px-6 md:px-10 lg:px-16">
        <div className="max-w-6xl mx-auto">

          {/* Breadcrumbs */}
          <nav className="flex items-center gap-1.5 text-sm text-muted-foreground mb-8 flex-wrap" aria-label="Breadcrumb">
            <Link href="/homepage" className="hover:text-primary transition-colors font-medium">
              {l.home}
            </Link>
            <svg className="w-3.5 h-3.5 text-muted-foreground/40 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <Link href="/products" className="hover:text-primary transition-colors font-medium">
              {l.products}
            </Link>
            <svg className="w-3.5 h-3.5 text-muted-foreground/40 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <span className="text-foreground font-semibold truncate max-w-[200px]">{productName}</span>
          </nav>

          {/* 2-Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 xl:gap-16 items-start">

            {/* LEFT: Product Image */}
            <div className="w-full">
              <ProductImage src={product.image} alt={product.alt || productName} />
            </div>

            {/* RIGHT: Product Details */}
            <div className="flex flex-col gap-5">

              {/* Product Title */}
              <div>
                <h1 className="font-sans text-3xl md:text-4xl xl:text-5xl font-extrabold text-foreground uppercase tracking-tight leading-tight mb-2">
                  {productName}
                </h1>
                {/* Scientific Name */}
                <p className="text-primary italic text-lg font-medium mb-3">
                  {product.scientificName}
                </p>
                {/* Category */}
                <p className="text-foreground font-semibold text-sm uppercase tracking-widest">
                  {categoryLabel}
                </p>
              </div>

              {/* Technical Specifications Box */}
              <div className="border border-primary/30 rounded-lg overflow-hidden bg-white">
                <div className="px-5 py-3 bg-primary/5 border-b border-primary/20">
                  <h2 className="text-xs font-bold uppercase tracking-widest text-primary">
                    {l.technicalSpecs}
                  </h2>
                </div>
                <div className="divide-y divide-gray-100">
                  {specs.map((spec, idx) => (
                    <div key={idx} className="flex items-start gap-3 px-5 py-3.5">
                      <span className="text-sm font-bold text-foreground min-w-[110px] flex-shrink-0">
                        {spec.label}:
                      </span>
                      <span className="text-sm text-muted-foreground leading-relaxed">
                        {spec.value}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* CTA Button */}
              <div className="pt-1">
                <button
                  onClick={handleRequestQuote}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2.5 bg-primary hover:bg-primary/90 text-white font-bold text-base px-8 py-4 rounded-lg transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 uppercase tracking-wide"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  {l.requestQuote}
                </button>
                <p className="text-xs text-muted-foreground mt-3 leading-relaxed max-w-sm">
                  {l.b2bNote}
                </p>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* WhatsApp Floating Button */}
      <a
        href="https://wa.me/14506011665"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-green-500 hover:bg-green-600 text-white rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-200 hover:-translate-y-1"
        aria-label="Contact via WhatsApp"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="w-7 h-7" fill="currentColor" viewBox="0 0 24 24">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
        </svg>
      </a>

      <Footer />
    </main>
  );
}

export default function ProductDetailPage() {
  return (
    <Suspense fallback={
      <main className="min-h-screen bg-background">
        <div className="pt-40 pb-24 px-6 md:px-12 flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </main>
    }>
      <ProductDetailContent />
    </Suspense>
  );
}
